// src/pages/AuthModal.jsx
import { useState, useEffect } from "react";
import config from "../config";

const { API_BASE } = config;

async function postJSON(url, body) {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
}

function Field({ label, type = "text", value, onChange, placeholder, autoComplete, required }) {
  return (
    <div className="am-field">
      <label className="am-field__label">
        {label}{required && <span className="am-field__req" aria-hidden="true"> *</span>}
      </label>
      <input
        className="am-field__input"
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required={required}
      />
    </div>
  );
}

function LoginForm({ onLogin, onSwitch }) {
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");

  async function handleSubmit() {
    if (!email || !password) { setError("Please fill in all fields."); return; }
    setLoading(true);
    setError("");
    try {
      const data = await postJSON(`${API_BASE}/customer-login.php`, { email, password });
      if (!data.token || !data.customer) throw new Error("Invalid response from server.");
      onLogin(data.token, data.customer);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="am-form">
      <p className="am-form__intro">Welcome back! Sign in to your account.</p>
      {error && <div className="am-error" role="alert">{error}</div>}
      <Field label="Email"    type="email"    value={email}    onChange={setEmail}
        placeholder="you@example.com" autoComplete="email" required />
      <Field label="Password" type="password" value={password} onChange={setPassword}
        placeholder="••••••••" autoComplete="current-password" required />
      <button className="am-btn am-btn--primary" onClick={handleSubmit} disabled={loading}>
        {loading ? <span className="am-spinner" /> : null}
        {loading ? "Signing in…" : "Sign in"}
      </button>
      <p className="am-switch">
        Don't have an account?{" "}
        <button className="am-switch__link" onClick={onSwitch}>Create one</button>
      </p>
    </div>
  );
}

function RegisterForm({ onLogin, onSwitch }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    password: "", confirm: "",
    address: "", city: "", province: "", postal_code: "",
  });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const set = (key) => (val) => setForm((f) => ({ ...f, [key]: val }));

  async function handleSubmit() {
    if (form.password !== form.confirm) { setError("Passwords do not match."); return; }
    if (form.password.length < 8)       { setError("Password must be at least 8 characters."); return; }
    setLoading(true);
    setError("");
    try {
      const { confirm, ...payload } = form;
      const data = await postJSON(`${API_BASE}/customer-register.php`, payload);
      if (!data.token || !data.customer) throw new Error("Invalid response from server.");
      onLogin(data.token, data.customer);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="am-form">
      <p className="am-form__intro">Create a free account to track orders and save your wishlist.</p>
      {error && <div className="am-error" role="alert">{error}</div>}

      <div className="am-steps">
        <div className={`am-step ${step >= 1 ? "am-step--active" : ""}`}>
          <span className="am-step__dot">1</span>
          <span className="am-step__label">Account</span>
        </div>
        <div className="am-step__line" />
        <div className={`am-step ${step >= 2 ? "am-step--active" : ""}`}>
          <span className="am-step__dot">2</span>
          <span className="am-step__label">Delivery</span>
        </div>
      </div>

      {step === 1 && (
        <>
          <div className="am-row">
            <Field label="First name" value={form.first_name} onChange={set("first_name")} placeholder="Jane"  autoComplete="given-name"  required />
            <Field label="Last name"  value={form.last_name}  onChange={set("last_name")}  placeholder="Smith" autoComplete="family-name" required />
          </div>
          <Field label="Email"    type="email"    value={form.email}    onChange={set("email")}    placeholder="you@example.com"   autoComplete="email"        required />
          <Field label="Phone"    type="tel"      value={form.phone}    onChange={set("phone")}    placeholder="+27 82 000 0000"  autoComplete="tel" />
          <Field label="Password"         type="password" value={form.password} onChange={set("password")} placeholder="Min 8 characters" autoComplete="new-password" required />
          <Field label="Confirm password" type="password" value={form.confirm}  onChange={set("confirm")}  placeholder="Repeat password"  autoComplete="new-password" required />
          <button className="am-btn am-btn--primary" onClick={() => {
            if (!form.first_name || !form.last_name || !form.email || !form.password) {
              setError("Please fill in all required fields."); return;
            }
            setError(""); setStep(2);
          }}>
            Continue
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
              <path d="M4 10h12M11 5l5 5-5 5" />
            </svg>
          </button>
        </>
      )}

      {step === 2 && (
        <>
          <div className="am-optional-notice">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/>
            </svg>
            Delivery address is optional — you can add this later.
          </div>
          <Field label="Street address" value={form.address}     onChange={set("address")}     placeholder="12 Example Street" autoComplete="street-address" />
          <div className="am-row">
            <Field label="City"     value={form.city}     onChange={set("city")}     placeholder="Cape Town"    autoComplete="address-level2" />
            <Field label="Province" value={form.province} onChange={set("province")} placeholder="Western Cape" autoComplete="address-level1" />
          </div>
          <Field label="Postal code" value={form.postal_code} onChange={set("postal_code")} placeholder="8001" autoComplete="postal-code" />
          <div className="am-btn-row">
            <button className="am-btn am-btn--ghost" onClick={() => setStep(1)}>
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                <path d="M16 10H4M9 5l-5 5 5 5" />
              </svg>
              Back
            </button>
            <button className="am-btn am-btn--primary am-btn--flex" onClick={handleSubmit} disabled={loading}>
              {loading ? <span className="am-spinner" /> : null}
              {loading ? "Creating account…" : "Create account"}
            </button>
          </div>
        </>
      )}

      <p className="am-switch">
        Already have an account?{" "}
        <button className="am-switch__link" onClick={onSwitch}>Sign in</button>
      </p>
    </div>
  );
}

export function AuthModal({ isOpen, onClose, onLogin, initialTab = "login" }) {
  const [tab, setTab] = useState(initialTab);

  useEffect(() => { setTab(initialTab); }, [initialTab, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  // onLogin comes from App and calls AuthContext.login directly — no wrapper needed
  function handleLogin(token, customer) {
    onLogin(token, customer);
    onClose();
  }

  if (!isOpen) return null;

  return (
    <div
      className="am-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog" aria-modal="true"
      aria-label={tab === "login" ? "Sign in" : "Create account"}
    >
      <div className="am-modal">
        <div className="am-header">
          <div className="am-brand">
            <div className="am-brand__icon">
              <svg width="20" height="20" viewBox="0 0 32 32" fill="none" aria-hidden="true">
                <path d="M8 20c0-4.4 3.6-8 8-8s8 3.6 8 8" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
                <circle cx="16" cy="11" r="2.5" fill="#7eb8e8" />
              </svg>
            </div>
            <span className="am-brand__name">My Account</span>
          </div>
          <button className="am-close" onClick={onClose} aria-label="Close dialog">
            <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M4 4l12 12M16 4L4 16" />
            </svg>
          </button>
        </div>

        <div className="am-tabs" role="tablist">
          <button role="tab" aria-selected={tab === "login"}    className={`am-tab ${tab === "login"    ? "am-tab--active" : ""}`} onClick={() => setTab("login")}>Sign in</button>
          <button role="tab" aria-selected={tab === "register"} className={`am-tab ${tab === "register" ? "am-tab--active" : ""}`} onClick={() => setTab("register")}>Register</button>
        </div>

        <div className="am-body">
          {tab === "login"
            ? <LoginForm    onLogin={handleLogin} onSwitch={() => setTab("register")} />
            : <RegisterForm onLogin={handleLogin} onSwitch={() => setTab("login")} />}
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700;800&display=swap');
        .am-backdrop { position:fixed;inset:0;z-index:9000;background:rgba(1,15,50,.65);backdrop-filter:blur(6px);display:flex;align-items:center;justify-content:center;padding:20px;font-family:'Roboto',sans-serif; }
        .am-modal { position:relative;background:#fff;border-radius:20px;width:100%;max-width:440px;max-height:92vh;overflow-y:auto;box-shadow:0 0 0 1px rgba(1,61,151,.08),0 8px 16px rgba(1,15,50,.10),0 32px 80px rgba(1,15,50,.22);animation:am-slide-in .24s cubic-bezier(.34,1.3,.64,1); }
        @keyframes am-slide-in { from{opacity:0;transform:translateY(16px) scale(.96)} to{opacity:1;transform:translateY(0) scale(1)} }
        .am-header { display:flex;align-items:center;justify-content:space-between;padding:22px 24px 0; }
        .am-brand { display:flex;align-items:center;gap:10px; }
        .am-brand__icon { width:36px;height:36px;border-radius:10px;background:linear-gradient(135deg,#013d97,#0255cc);display:flex;align-items:center;justify-content:center;flex-shrink:0;box-shadow:0 2px 8px rgba(1,61,151,.35); }
        .am-brand__name { font-size:17px;font-weight:800;color:#0f1c38;letter-spacing:-.02em; }
        .am-close { width:32px;height:32px;border-radius:8px;border:1.5px solid #e8ecf3;background:#f7f9fc;cursor:pointer;display:flex;align-items:center;justify-content:center;color:#6b7a99;transition:all .15s; }
        .am-close:hover { background:#fee2e2;border-color:#fecaca;color:#dc2626; }
        .am-tabs { display:flex;margin:20px 24px 0;background:#f0f4fa;border-radius:10px;padding:4px; }
        .am-tab { flex:1;padding:9px 0;background:none;border:none;border-radius:7px;font-family:'Roboto',sans-serif;font-size:13.5px;font-weight:700;color:#7a88a8;cursor:pointer;transition:all .18s; }
        .am-tab:hover:not(.am-tab--active) { color:#013d97; }
        .am-tab--active { background:#fff;color:#013d97;box-shadow:0 1px 4px rgba(1,21,70,.12),0 0 0 1px rgba(1,61,151,.08); }
        .am-body { padding:22px 24px 28px; }
        .am-form { display:flex;flex-direction:column;gap:16px; }
        .am-form__intro { font-size:13.5px;color:#6b7a99;line-height:1.55; }
        .am-error { background:#fef2f2;border:1px solid #fecaca;color:#991b1b;padding:11px 14px;border-radius:10px;font-size:13px;font-weight:500;line-height:1.45; }
        .am-steps { display:flex;align-items:center; }
        .am-step { display:flex;align-items:center;gap:7px; }
        .am-step__dot { width:24px;height:24px;border-radius:50%;background:#e8ecf3;color:#7a88a8;font-size:11px;font-weight:800;display:flex;align-items:center;justify-content:center;flex-shrink:0; }
        .am-step--active .am-step__dot { background:#013d97;color:#fff;box-shadow:0 0 0 3px rgba(1,61,151,.15); }
        .am-step__label { font-size:12.5px;font-weight:700;color:#b0bac8; }
        .am-step--active .am-step__label { color:#013d97; }
        .am-step__line { flex:1;height:2px;background:#e8ecf3;margin:0 10px;border-radius:1px; }
        .am-optional-notice { display:flex;align-items:center;gap:7px;background:#f0f7ff;border:1px solid #bfdbfe;color:#1e40af;padding:10px 13px;border-radius:8px;font-size:12.5px;font-weight:500; }
        .am-field { display:flex;flex-direction:column;gap:6px;flex:1;min-width:0; }
        .am-field__label { font-size:12px;font-weight:700;color:#374151;letter-spacing:.02em;text-transform:uppercase; }
        .am-field__req { color:#ef4444; }
        .am-field__input { width:100%;border:1.5px solid #e2e8f0;border-radius:10px;padding:11px 14px;font-family:'Roboto',sans-serif;font-size:14px;color:#0f1c38;background:#fafbfd;outline:none;transition:border-color .18s,box-shadow .18s;box-sizing:border-box; }
        .am-field__input:focus { border-color:#013d97;background:#fff;box-shadow:0 0 0 3px rgba(1,61,151,.10); }
        .am-field__input::placeholder { color:#b8c2d8; }
        .am-row { display:flex;gap:12px; }
        .am-btn-row { display:flex;gap:10px;align-items:center; }
        .am-btn { display:inline-flex;align-items:center;justify-content:center;gap:7px;padding:12px 20px;border-radius:10px;border:none;cursor:pointer;font-family:'Roboto',sans-serif;font-size:14px;font-weight:700;transition:all .18s;width:100%; }
        .am-btn:disabled { opacity:.55;cursor:not-allowed; }
        .am-btn--primary { background:linear-gradient(135deg,#013d97,#0255cc);color:#fff;box-shadow:0 2px 8px rgba(1,61,151,.30); }
        .am-btn--primary:hover:not(:disabled) { background:linear-gradient(135deg,#012f77,#0147aa);transform:translateY(-1px); }
        .am-btn--ghost { background:#f0f4fa;color:#5a6a8a;border:1.5px solid #e2e8f0;width:auto;flex-shrink:0; }
        .am-btn--ghost:hover { background:#e4ebf5;color:#013d97; }
        .am-btn--flex { flex:1;width:auto; }
        .am-spinner { width:15px;height:15px;border:2px solid rgba(255,255,255,.3);border-top-color:#fff;border-radius:50%;animation:am-spin .6s linear infinite;flex-shrink:0; }
        @keyframes am-spin { to{transform:rotate(360deg)} }
        .am-switch { font-size:13px;color:#8895b0;text-align:center; }
        .am-switch__link { background:none;border:none;cursor:pointer;font-family:inherit;font-size:inherit;font-weight:700;color:#013d97;padding:0;text-decoration:underline; }
        @media(max-width:480px) { .am-backdrop{align-items:flex-end;padding:0} .am-modal{border-radius:20px 20px 0 0;max-height:95vh} .am-row{flex-direction:column} }
        .am-modal *:focus-visible { outline:2px solid #029add;outline-offset:2px; }
      `}</style>
    </div>
  );
}

export default AuthModal;